import { Chip, Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { sortBy } from 'lodash';
import { useMemo } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import { KeyValueResponsive } from '@/components/KeyValueResponsive';
import type { KeyValueResponsiveModel } from '@/components/KeyValueResponsive/KeyValueResponsive.types';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import { caseDetailsAtom } from '@/pages/Case/Case.store';
import {
  CASE_ACTIVITY_TYPE,
  CASE_SOC_REVIEW_EXTERNAL_COLORS
} from '@/pages/Cases';
import { getReviewedStatus } from '@/pages/Cases/Cases.utils';
import { userIsInternalAtom } from '@/store/user.store';
import getDateTime from '@/utils/getDateTime.utils';

import useStyles from './CaseSocReview.styles';

const CaseSocReview = () => {
  const { classes } = useStyles();
  const caseDetails = useAtomValue(caseDetailsAtom);
  const userIsInternal = useAtomValue(userIsInternalAtom);

  const socReviewActivity = useMemo(() => {
    const activities = caseDetails?.activities || [];
    const reviewActivities = activities.filter(
      (activity) => activity.type === CASE_ACTIVITY_TYPE.SOC_REVIEW
    );
    if (!reviewActivities.length) {
      return undefined;
    }

    return sortBy(reviewActivities, 'created_at').reverse()[0];
  }, [caseDetails?.activities]);

  const color = useMemo(() => {
    if (socReviewActivity?.message?.includes('Properly')) {
      return 'success';
    }

    if (socReviewActivity?.message?.includes('Improperly')) {
      return 'error';
    }

    return undefined;
  }, [socReviewActivity?.message]);

  const kvItems: KeyValueResponsiveModel[] = [
    {
      label: 'Reviewed By',
      value: socReviewActivity?.user?.name || '--'
    },
    {
      label: 'Reviewed At',
      value: socReviewActivity?.created_at
        ? getDateTime(socReviewActivity.created_at)
        : '--'
    }
  ];

  if (userIsInternal) {
    kvItems.push({
      label: 'Reason',
      value: `${socReviewActivity?.data?.body || '--'}`,
      className: classes.reason
    });
  }

  const externalUserReviewStatus = useMemo(
    () =>
      getReviewedStatus(
        socReviewActivity?.message,
        caseDetails?.priority || ''
      ),
    [socReviewActivity?.message, caseDetails?.priority]
  );

  if (!socReviewActivity) {
    return <></>;
  }

  return (
    <LargeCard title="SOC Review" className={classes.caseSocReview}>
      <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
        <CheckPermissions permissions={[PERMISSIONS.INTERNAL]}>
          {socReviewActivity?.message ? (
            <Chip
              label={socReviewActivity.message}
              color={color as 'success' | 'error' | undefined}
              variant="outlined"
            />
          ) : (
            <Typography sx={{ fontStyle: 'italic' }}>
              No SOC review provided yet
            </Typography>
          )}
          {socReviewActivity?.message && <KeyValueResponsive items={kvItems} />}
        </CheckPermissions>

        <CheckPermissions permissions={[PERMISSIONS.NOT_INTERNAL]}>
          <Chip
            label={externalUserReviewStatus}
            color={CASE_SOC_REVIEW_EXTERNAL_COLORS[externalUserReviewStatus]}
            variant="outlined"
          />
        </CheckPermissions>
      </Stack>
    </LargeCard>
  );
};

export default CaseSocReview;
